import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import SEO from "../../components/site/SEO";
import { inputCls, labelCls, btnPrimaire } from "./ui";

const SERVICES = [
  { id: "pneus", label: "Pneumatique" },
  { id: "parallelisme", label: "Parallélisme" },
  { id: "climatisation", label: "Climatisation" },
  { id: "mecanique", label: "Mécanique" },
];

export default function AdminTarifs() {
  const [tarifs, setTarifs] = useState({});
  const [onglet, setOnglet] = useState("pneus");
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    api.get("/admin/tarifs")
      .then((r) => {
        const map = {};
        r.data.forEach((t) => { map[t.service] = t; });
        setTarifs(map);
      })
      .catch(() => {})
      .finally(() => setChargement(false));
  }, []);

  const courant = tarifs[onglet] || { titre: "", rows: [] };

  const majLigne = (i, champ, valeur) => {
    const rows = courant.rows.map((r, j) => (j === i ? { ...r, [champ]: valeur } : r));
    setTarifs({ ...tarifs, [onglet]: { ...courant, rows } });
  };

  const ajouterLigne = () =>
    setTarifs({ ...tarifs, [onglet]: { ...courant, rows: [...courant.rows, { prestation: "", prix: "" }] } });

  const retirerLigne = (i) =>
    setTarifs({ ...tarifs, [onglet]: { ...courant, rows: courant.rows.filter((_, j) => j !== i) } });

  const enregistrer = async () => {
    try {
      await api.put(`/admin/tarifs/${onglet}`, { titre: courant.titre, rows: courant.rows });
      toast.success("Les tarifs ont été enregistrés. Ils sont visibles sur le site.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-tarifs">
      <SEO title="Tarifs — Espace gérant | Promo Pneus" description="Modifier les tarifs affichés sur le site." />
      <h1 className="font-heading font-bold uppercase text-4xl text-brand-dark tracking-tight mb-2">Tarifs</h1>
      <p className="text-brand-body text-lg mb-6">
        Modifiez les prix ci-dessous puis cliquez sur « Enregistrer ». Le site se met à jour partout, y compris sur la page d'accueil.
      </p>

      <div className="flex gap-2 mb-6 flex-wrap" role="tablist" aria-label="Choisir le tableau de tarifs">
        {SERVICES.map((s) => (
          <button key={s.id} role="tab" aria-selected={onglet === s.id}
            data-testid={`tarifs-onglet-${s.id}`}
            onClick={() => setOnglet(s.id)}
            className={`font-heading font-semibold uppercase tracking-wider text-lg px-6 py-3.5 transition-colors ${
              onglet === s.id ? "bg-brand-red text-white" : "bg-white text-brand-dark hover:bg-gray-100"
            }`}>
            {s.label}
          </button>
        ))}
      </div>

      {chargement ? (
        <p className="text-brand-body text-lg">Chargement des tarifs…</p>
      ) : (
        <div className="bg-white p-6 shadow-sm space-y-4">
          <div className="hidden sm:grid grid-cols-[1fr_220px_60px] gap-3">
            <span className={labelCls}>Nom de la prestation</span>
            <span className={labelCls}>Prix affiché</span>
            <span />
          </div>
          {courant.rows.map((r, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_220px_60px] gap-3 items-center">
              <input data-testid={`tarif-prestation-${i}`} className={inputCls} value={r.prestation}
                onChange={(e) => majLigne(i, "prestation", e.target.value)}
                placeholder="Ex. : Montage de pneumatiques" aria-label={`Prestation ligne ${i + 1}`} />
              <input data-testid={`tarif-prix-${i}`} className={inputCls} value={r.prix}
                onChange={(e) => majLigne(i, "prix", e.target.value)}
                placeholder="Ex. : à partir de 20 €" aria-label={`Prix ligne ${i + 1}`} />
              <button data-testid={`tarif-supprimer-${i}`} onClick={() => retirerLigne(i)}
                className="p-3.5 bg-red-50 hover:bg-red-100 text-brand-redhover transition-colors justify-self-start"
                aria-label={`Supprimer la ligne ${i + 1}`}>
                <Trash2 size={22} />
              </button>
            </div>
          ))}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button data-testid="tarif-ajouter-ligne" onClick={ajouterLigne}
              className="inline-flex items-center justify-center gap-2 bg-brand-dark hover:bg-black text-white font-heading font-semibold uppercase tracking-wider text-lg px-6 py-4 transition-colors">
              <Plus size={20} aria-hidden="true" /> Ajouter une ligne
            </button>
            <button data-testid="tarifs-enregistrer" onClick={enregistrer} className={btnPrimaire}>
              Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
